import { jsx } from 'react/jsx-runtime';
import { createContext, useMemo, useCallback, useState, useContext } from 'react';
import '../MenuContext/index.mjs';
import { SiteId } from '../MenuContext/mapping.mjs';
import { isUrlPathname, isPartOfUrlPathname } from '../MenuContext/utils.mjs';
import { MICROSITE_HEADER_MAPPING, DEFAULT_LANGUAGE } from './mapping.mjs';
import { useMenu } from '../MenuContext/context.mjs';
"use strict";
const MicrositeMenuContext = createContext(null);
const MicrositeMenuContextProvider = ({
  context,
  pathname: currentPathname = "/",
  hash: currentHash = "",
  children,
  language = DEFAULT_LANGUAGE
}) => {
  const {
    createSiteUrl
  } = useMenu();
  const isUIContext = useMemo(() => context === SiteId.Ui, [context]);
  const isSiteUrlActive = useCallback(url => {
    return (
      // Do not check for active items if not in UI context
      isUIContext &&
      // Check if the siteId matches the current context
      url[0] === context &&
      // Check if the pathname matches the current pathname
      isUrlPathname(currentPathname, url[1]) && (
      // If there's a hash in the SiteUrl, it should match the current hash for the item to be active
      !url[3] || currentHash.replace(/^#/, "") === url[3])
    );
  }, [context, currentHash, currentPathname, isUIContext]);
  const parseMenuItem = useCallback(item => {
    const childItems = item?.items?.map(parseMenuItem).filter(item2 => !!item2);
    const active =
    // If any child is active, the parent is active
    childItems?.some(item2 => item2.active) || childItems?.some(item2 => item2.possible) ||
    // If the item has a URL, check if it's active
    item.url && typeof item.url !== "string" && isSiteUrlActive(item.url);
    const url = item.url ? typeof item.url === "string" ? item.url : createSiteUrl(item.url) : childItems?.[0]?.url;
    const possible = item.url && typeof item.url !== "string" && item.url[0] === SiteId.Ui && isPartOfUrlPathname(currentPathname, item.url[1]);
    if (!url) {
      return null;
    }
    return {
      ...item,
      label: item.label,
      collapsedLabel: item?.collapsedLabel,
      description: item?.description,
      actionLabel: item?.actionLabel,
      active: !!active,
      possible: !!possible,
      items: childItems,
      url
    };
  }, [createSiteUrl, currentPathname, isSiteUrlActive]);
  const menu = useMemo(() => {
    return parseMenuItem(MICROSITE_HEADER_MAPPING);
  }, [parseMenuItem]);
  const [{
    activeModal,
    prevActiveModal
  }, _setActiveModal] = useState({
    activeModal: null,
    prevActiveModal: null
  });
  const setActiveModal = useCallback(id => {
    _setActiveModal({
      activeModal: id,
      prevActiveModal: activeModal
    });
  }, [activeModal]);
  return /* @__PURE__ */jsx(MicrositeMenuContext.Provider, {
    value: {
      menu,
      language,
      activeModal,
      prevActiveModal,
      setActiveModal
    },
    children
  });
};
function useMicrositeMenu() {
  const context = useContext(MicrositeMenuContext);
  if (context === null) {
    throw new Error("useMicrositeMenu must be used within a MicrositeMenuContextProvider");
  }
  return context;
}
export { MicrositeMenuContext, MicrositeMenuContextProvider, useMicrositeMenu };
//# sourceMappingURL=context.mjs.map
